import React, { createContext, FC, useEffect } from 'react'
import io, { Socket } from 'socket.io-client'

const ENDPOINT = import.meta.env.VITE_BASE_URL
const RECONNECT_DELAY_MS = 5000

type Props = {
    children: React.ReactNode
}

type SocketContextType = {
    socket: Socket
    acquireConnection: () => void
    releaseConnection: () => void
}

export const SocketContext = createContext<SocketContextType | undefined>(
    undefined,
)

export const SocketProvider: FC<Props> = ({ children }) => {
    const socketRef = React.useRef<Socket | null>(null)
    const consumersRef = React.useRef(0)
    const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )
    const isUnmountedRef = React.useRef(false)

    if (!socketRef.current) {
        socketRef.current = io(ENDPOINT, {
            autoConnect: false,
            reconnection: false,
        })
    }

    const socket = socketRef.current

    const clearReconnectTimeout = () => {
        if (!retryTimeoutRef.current) {
            return
        }

        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
    }

    const scheduleReconnect = () => {
        if (
            isUnmountedRef.current ||
            retryTimeoutRef.current ||
            consumersRef.current <= 0
        ) {
            return
        }

        retryTimeoutRef.current = setTimeout(() => {
            retryTimeoutRef.current = null

            if (!socket.connected && consumersRef.current > 0) {
                socket.connect()
            }
        }, RECONNECT_DELAY_MS)
    }

    const acquireConnection = () => {
        consumersRef.current += 1

        if (!socket.connected) {
            socket.connect()
        }
    }

    const releaseConnection = () => {
        consumersRef.current = Math.max(0, consumersRef.current - 1)

        if (consumersRef.current === 0) {
            clearReconnectTimeout()

            if (socket.connected) {
                socket.disconnect()
            }
        }
    }

    useEffect(() => {
        socket.on('connect', () => {
            clearReconnectTimeout()
            console.log('Conectado ao WebSocket')
        })

        socket.on('connect_error', err => {
            console.error('Erro ao conectar ao WebSocket:', err)
            scheduleReconnect()
        })

        socket.on('disconnect', reason => {
            console.log('Desconectado do WebSocket:', reason)

            if (reason === 'io client disconnect') {
                return
            }

            scheduleReconnect()
        })

        return () => {
            isUnmountedRef.current = true
            consumersRef.current = 0
            clearReconnectTimeout()
            socket.removeAllListeners()
            socket.disconnect()
        }
    }, [socket])

    return (
        <SocketContext.Provider
            value={{ socket, acquireConnection, releaseConnection }}
        >
            {children}
        </SocketContext.Provider>
    )
}
